import React, { useState, useEffect, useRef } from "react";
import { MohammadAvatarSvg } from "./MohammadAvatarSvg";

interface TehranTimeState {
  formatted: string;
  isSleepingTime: boolean;
  hour: number;
}

const getTehranTime = (): TehranTimeState => {
  try {
    const now = new Date();
    const formatted = now.toLocaleTimeString("en-US", {
      timeZone: "Asia/Tehran",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Tehran",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    }).formatToParts(now);

    const hourPart = parts.find((p) => p.type === "hour")?.value;
    const minutePart = parts.find((p) => p.type === "minute")?.value;
    const hour = parseInt(hourPart || "0", 10);
    const minute = parseInt(minutePart || "0", 10);
    const totalMinutes = hour * 60 + minute;

    // Resting between 10:00 PM (1320 min) and 9:00 AM (540 min)
    const isSleepingTime = totalMinutes >= 1320 || totalMinutes < 540;

    return { formatted, isSleepingTime, hour };
  } catch {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const tehranDate = new Date(utc + 3600000 * 3.5);
    const hour = tehranDate.getHours();
    const isSleepingTime = hour >= 22 || hour < 9;
    return {
      formatted: tehranDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      isSleepingTime,
      hour,
    };
  }
};

interface MohammadAvatarProps {
  className?: string;
  onToggleState?: (isSleeping: boolean) => void;
}

export const MohammadAvatar: React.FC<MohammadAvatarProps> = ({
  className = "w-56 h-64 sm:w-64 sm:h-72 lg:w-72 lg:h-80",
  onToggleState,
}) => {
  const [tehranTime, setTehranTime] = useState<TehranTimeState>(() => getTehranTime());
  const [manualOverride, setManualOverride] = useState<boolean | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [pupilOffset, setPupilOffset] = useState({ x: 0, y: 0 });
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const targetOffset = useRef({ x: 0, y: 0 });
  const currentOffset = useRef({ x: 0, y: 0 });
  const targetTilt = useRef({ x: 0, y: 0 });
  const currentTilt = useRef({ x: 0, y: 0 });
  const animFrameId = useRef<number | null>(null);
  const cachedRect = useRef<DOMRect | null>(null);
  const lastRectTime = useRef(0);

  // Determine current active mode
  const isNaturalSleep = tehranTime.isSleepingTime;
  const isSleeping = manualOverride !== null ? manualOverride : isNaturalSleep;
  const showSleepingEyes = isSleeping;

  // Clock ticker
  useEffect(() => {
    const tick = () => {
      const state = getTehranTime();
      setTehranTime(state);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  // Eye tracking & subtle 3D tilt animation loop
  useEffect(() => {
    const getBounds = () => {
      const now = performance.now();
      if ((!cachedRect.current || now - lastRectTime.current > 200) && containerRef.current) {
        cachedRect.current = containerRef.current.getBoundingClientRect();
        lastRectTime.current = now;
      }
      return cachedRect.current;
    };

    const handlePointerMove = (e: MouseEvent | TouchEvent) => {
      if (showSleepingEyes) return;
      const bounds = getBounds();
      if (!bounds) return;

      let clientX = 0;
      let clientY = 0;
      if ("touches" in e && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else if ("clientX" in e) {
        clientX = e.clientX;
        clientY = e.clientY;
      } else {
        return;
      }

      // Eye height is ~37% down the avatar SVG
      const eyeCenterX = bounds.left + bounds.width * 0.5;
      const eyeCenterY = bounds.top + bounds.height * 0.37;

      const dx = clientX - eyeCenterX;
      const dy = clientY - eyeCenterY;
      const dist = Math.hypot(dx, dy);
      const angle = Math.atan2(dy, dx);

      // Clamped pupil movement in SVG coordinate units
      const maxRadiusX = 28;
      const maxRadiusY = 22;
      const damping = Math.tanh(dist / 260);

      targetOffset.current = {
        x: Math.cos(angle) * maxRadiusX * damping,
        y: Math.sin(angle) * maxRadiusY * damping,
      };

      // Subtle 3D card tilt for depth
      const deltaX = (clientX - (bounds.left + bounds.width * 0.5)) / (window.innerWidth * 0.5);
      const deltaY = (clientY - (bounds.top + bounds.height * 0.5)) / (window.innerHeight * 0.5);
      const clampedX = Math.max(-1, Math.min(1, deltaX));
      const clampedY = Math.max(-1, Math.min(1, deltaY));

      targetTilt.current = {
        x: clampedX * 5,
        y: -clampedY * 4,
      };
    };

    const handlePointerLeave = () => {
      targetOffset.current = { x: 0, y: 0 };
      targetTilt.current = { x: 0, y: 0 };
    };

    const handleInvalidateBounds = () => {
      cachedRect.current = null;
    };

    let lastTime = performance.now();
    const renderFrame = (now: number) => {
      const dt = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      const target = showSleepingEyes ? { x: 0, y: 0 } : targetOffset.current;
      const targetT = showSleepingEyes ? { x: 0, y: 2 } : targetTilt.current;

      const pupilSmoothing = 1 - Math.exp(-22 * dt);
      const tiltSmoothing = 1 - Math.exp(-14 * dt);

      currentOffset.current.x += (target.x - currentOffset.current.x) * pupilSmoothing;
      currentOffset.current.y += (target.y - currentOffset.current.y) * pupilSmoothing;

      currentTilt.current.x += (targetT.x - currentTilt.current.x) * tiltSmoothing;
      currentTilt.current.y += (targetT.y - currentTilt.current.y) * tiltSmoothing;

      setPupilOffset({
        x: currentOffset.current.x,
        y: currentOffset.current.y,
      });

      setTilt({
        x: Number(currentTilt.current.x.toFixed(2)),
        y: Number(currentTilt.current.y.toFixed(2)),
      });

      animFrameId.current = requestAnimationFrame(renderFrame);
    };

    window.addEventListener("mousemove", handlePointerMove, { passive: true });
    window.addEventListener("touchmove", handlePointerMove, { passive: true });
    window.addEventListener("scroll", handleInvalidateBounds, { passive: true });
    window.addEventListener("resize", handleInvalidateBounds, { passive: true });
    document.addEventListener("mouseleave", handlePointerLeave);
    window.addEventListener("blur", handlePointerLeave);

    animFrameId.current = requestAnimationFrame(renderFrame);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("scroll", handleInvalidateBounds);
      window.removeEventListener("resize", handleInvalidateBounds);
      document.removeEventListener("mouseleave", handlePointerLeave);
      window.removeEventListener("blur", handlePointerLeave);
      if (animFrameId.current) {
        cancelAnimationFrame(animFrameId.current);
      }
    };
  }, [showSleepingEyes]);

  const toggleSleep = () => {
    const nextState = !isSleeping;
    setManualOverride(nextState);
    if (onToggleState) onToggleState(nextState);
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <div
        id="hero-avatar-container"
        ref={containerRef}
        role="button"
        tabIndex={0}
        onClick={toggleSleep}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleSleep();
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered(true)}
        onTouchEnd={() => setIsHovered(false)}
        className={`relative flex items-center justify-center select-none group cursor-pointer transition-transform duration-200 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E5983A]/60 rounded-3xl ${className}`}
        title={
          isSleeping
            ? "Resting (10:00 PM – 9:00 AM IRST) · Click to wake up"
            : "Awake · Interactive eye tracking · Click to rest"
        }
        aria-label={
          isSleeping
            ? "Vector illustration of Mohammad Hosein Roustaie - Resting. Click to wake."
            : "Vector illustration of Mohammad Hosein Roustaie - Awake. Click to rest."
        }
      >
        <div
          className="w-full h-full flex items-center justify-center relative transition-transform duration-100 will-change-transform group-hover:scale-[1.02]"
          style={{
            transform: `perspective(900px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
            transformStyle: "preserve-3d",
          }}
        >
          {/* Subtle Ambient Backlight in Dark Mode */}
          <div
            className="absolute inset-4 rounded-full bg-[#E5983A]/10 blur-2xl pointer-events-none -z-10 opacity-70 group-hover:opacity-100 transition-opacity"
            aria-hidden="true"
          />

          {/* Pure Vector Interactive SVG Avatar */}
          <MohammadAvatarSvg
            pupilOffset={pupilOffset}
            isSleeping={showSleepingEyes}
            className="w-full h-full object-contain filter drop-shadow-2xl transition-all"
          />
        </div>
      </div>

      {/* Under-Avatar Telemetry Pill */}
      <div className="mt-3 text-center">
        <button
          type="button"
          onClick={toggleSleep}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#181716]/80 hover:bg-[#201F1D] border border-[#2A2926] transition-colors group/pill focus:outline-none focus:ring-1 focus:ring-[#E5983A]/50"
        >
          <span
            className={`w-2 h-2 rounded-full transition-colors ${
              isSleeping ? "bg-[#7A7870]" : "bg-[#22C55E] animate-pulse"
            }`}
          />
          <span className="text-[11px] font-mono text-[#9E9C94] group-hover/pill:text-[#E5983A] uppercase tracking-wider whitespace-nowrap transition-colors">
            TEH, IRN · IRST {tehranTime.formatted || "02:45 PM"} ·{" "}
            {isSleeping ? "RESTING" : "ACTIVE"}
          </span>
        </button>
      </div>
    </div>
  );
};
